import { TenantUserSettings } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { TenantsContext } from './tenantsContext';
import { getTenantShortNameFromUrl } from './utils';

export const TenantsProvider = ({ children }: { children: ReactNode }) => {
  const [tenant, setTenant] = useState<TenantUserSettings | undefined>();
  const { data: user } = useGetCurrentUser({ skipRedirect: true, retry: false });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const storedTenant = JSON.parse(localStorage.getItem('tenant') || '{}');
    const urlTenantShortName = getTenantShortNameFromUrl(globalThis.location.pathname);

    const isStillValid = user.user?.settings?.find(s => s.tenantId === storedTenant.tenantId && s.userEnabled);

    if (isStillValid && (!urlTenantShortName || storedTenant.tenantShortName === urlTenantShortName)) {
      _setTenant(storedTenant);
    }
  }, [user]);

  useEffect(() => {
    const urlTenantShortName = getTenantShortNameFromUrl(globalThis.location.pathname);
    const urlTenant = user?.user?.settings?.find(
      tenant => tenant.tenantShortName === urlTenantShortName && tenant.userEnabled
    );

    if (urlTenant && urlTenant.tenantId !== tenant?.tenantId) {
      _setTenant(urlTenant);
    }
  }, [user?.user?.settings]);

  const _setTenant = async (_tenant: TenantUserSettings) => {
    const previousTenantId = tenant?.tenantId;
    const newTenantId = _tenant.tenantId;

    if (previousTenantId && newTenantId && previousTenantId !== newTenantId) {
      await queryClient.cancelQueries();
    }

    localStorage.setItem('tenant', JSON.stringify(_tenant));
    setTenant(_tenant);
  };

  const getTenantId = useCallback(() => {
    return tenant?.tenantId || '';
  }, [tenant]);

  const getPath = useCallback(
    (path: string) => {
      return `/tenants/${tenant?.tenantShortName}/${path}`;
    },
    [tenant]
  );

  const getTenantIdFromShortName = useCallback(
    (shortName: string): string | undefined => {
      const foundTenant = user?.user?.settings?.find(
        tenant => tenant.tenantShortName === shortName && tenant.userEnabled
      );
      return foundTenant?.tenantId;
    },
    [user?.user?.settings]
  );

  return (
    <TenantsContext.Provider
      value={{
        tenant,
        setTenant: _setTenant,
        getPath,
        getTenantId,
        getTenantIdFromShortName,
      }}
    >
      {children}
    </TenantsContext.Provider>
  );
};
